/**
 * BlockRenderContext
 *
 * Provides render mode and utilities to all block renderers.
 * The mode determines which behavior wrappers are applied around visual components.
 */

import React, { createContext, useContext } from 'react'
import type { BlockRenderContextValue } from './Types'

const BlockRenderContext = createContext<BlockRenderContextValue | null>(null)

interface BlockRenderProviderProps {
  value: BlockRenderContextValue
  children: React.ReactNode
}

/**
 * Provider component that makes render context available to all descendant block renderers
 *
 * @example
 * ```tsx
 * <BlockRenderProvider value={{
 *   mode: 'editor',
 *   zoom: 100,
 *   coordinateTransform: (p) => ({ x: p.x * scale, y: p.y * scale }),
 *   selectedIds: new Set(['block-1']),
 *   onSelect: (id) => setSelected(id)
 * }}>
 *   <BlockRenderer block={myBlock} />
 * </BlockRenderProvider>
 * ```
 */
export function BlockRenderProvider({ value, children }: BlockRenderProviderProps) {
  return <BlockRenderContext.Provider value={value}>{children}</BlockRenderContext.Provider>
}

/**
 * Hook to access block render context
 *
 * @throws Error if used outside BlockRenderProvider
 *
 * @example
 * ```tsx
 * function MyVisual({ block }) {
 *   const { zoom, coordinateTransform } = useBlockRenderContext()
 *   const pixelPos = coordinateTransform({ x: block.x, y: block.y })
 *   return <div style={{ left: pixelPos.x, top: pixelPos.y }} />
 * }
 * ```
 */
export function useBlockRenderContext(): BlockRenderContextValue {
  const context = useContext(BlockRenderContext)

  if (!context) {
    throw new Error('useBlockRenderContext must be used within a BlockRenderProvider')
  }

  return context
}
